import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from './client'
import { ApiError, NetworkError } from './errors'
import { timestamp } from './schemas'

/**
 * `GET /app/config` — the bootstrap call.
 *
 * It must resolve before the router renders: it carries the kill switch, the
 * force-update gate, and the feature flags that decide whether two whole flows
 * exist at all. The crypto rails and orderbook trading cannot ship as drawn, so
 * the client hides them rather than hitting a 404.
 *
 * This route is **always live** — it is not behind `VITE_LIVE_DOMAINS`, because
 * the flags that decide whether whole flows exist must not depend on an env var
 * (see `domains.ts`). The fallback below is therefore the only thing standing
 * between an unreachable backend and an app that will not boot.
 *
 * There is one fixture, and it is not an exception to that rule: `?tether=1`
 * registers a handler ahead of the live gate to publish `tether_markets: true`,
 * because no backend lists a Tether market and there is otherwise no way to
 * reach the surface at all. `?referral=1` does the same for `referral`, which
 * stays false until core's PR #25 and this app are both deployed,
 * `?support_chat=1` for the Goftino chat, which is seeded off, and
 * `?withdraw=1` and `?deposit=1` for the two Toman flags, seeded off too. All
 * of them are per-tab, off by default, and pass the request through untouched
 * when unset — so this route is still always live in every build. See
 * `mocks/handlers.ts`.
 */

export const featureFlagsSchema = z.object({
  orderbook_trading: z.boolean(),
  crypto_deposit: z.boolean(),
  crypto_withdrawal: z.boolean(),
  credit_wallet: z.boolean(),
  referral: z.boolean(),
  /**
   * Whether any market is quoted in something other than Rial.
   *
   * Derived by the service from the listed markets and the seeding guard, not
   * stored — so it is false until a Tether market is actually listed, and the
   * client never has to infer the answer from a market symbol.
   *
   * Required, like every flag beside it. It is tempting to make this one
   * optional so an older backend still boots, but `/app/config` is the
   * always-live bootstrap route and a flag that silently defaults to `false`
   * is a second source of truth for whether a whole surface exists. A missing
   * field here is a version skew between this build and the service, and the
   * loud failure is the correct one — deploy core's §9 change before this app.
   */
  tether_markets: z.boolean(),
  /**
   * Whether the Goftino live chat (§19) is offered at all.
   *
   * Derived by core, like `tether_markets`: true only while an operator has
   * switched the chat on **and** the deployment has a widget id. Required for
   * the same reason as the flag above, so a core that does not send it fails
   * the parse and core's §19 change deploys first.
   */
  support_chat: z.boolean(),
  /**
   * Whether a Toman withdrawal can be started (§15).
   *
   * Derived by core: true only while an operator has switched it on **and**
   * the deployment has Jibit Cobank credentials and a connected account.
   * Every entry point hides while it is false; a withdrawal already in flight
   * stays readable and cancellable, so `/withdraw/:id` is not gated on it.
   * Required like its neighbours, so core's §15 change deploys first.
   */
  toman_withdrawal: z.boolean(),
  /**
   * Whether a Toman deposit can be made (§11): «انتقال بانکی» and
   * «کارت‌به‌کارت» to the company account, from the user's own accounts.
   *
   * Derived by core like its withdrawal twin: the operator switch **and** the
   * Jibit Cobank keys. While it is false both methods and every Toman-deposit
   * entry point hide; a deposit already made stays readable at
   * `/deposit/:id`. Required like its neighbours, so core 2A deploys first.
   */
  toman_deposit: z.boolean(),
})

/**
 * The widget `support_chat` names. `null` whenever the feature is off, so the
 * key is required and nullable rather than optional: a missing block is a
 * version skew, and a `null` one is an answer.
 *
 * The id is public — it ships in every page that embeds the widget — and is
 * what a guest's chat loads. A signed-in chat loads the id that came with its
 * session instead (`/support/chat/session`).
 */
export const supportChatConfigSchema = z.object({
  provider: z.literal('goftino'),
  widget_id: z.string().min(1),
})

export const appConfigSchema = z.object({
  min_supported_version: z.string(),
  latest_version: z.string(),
  force_update: z.boolean(),
  trading_enabled: z.boolean(),
  kill_switch: z.object({
    enabled: z.boolean(),
    reason_fa: z.string().nullable(),
  }),
  features: featureFlagsSchema,
  links: z.object({
    terms: z.string(),
    privacy: z.string(),
    support_phone: z.string(),
  }),
  support_chat: supportChatConfigSchema.nullable(),
  server_time: timestamp,
})

export type AppConfig = z.infer<typeof appConfigSchema>
export type FeatureFlags = z.infer<typeof featureFlagsSchema>
export type FeatureName = keyof FeatureFlags

/**
 * What the client assumes when the backend cannot answer `/app/config`.
 *
 * The service **does** serve this route — it is mounted first, and its payload
 * matches the schema above field for field. This is the net for the cases where
 * an answer still does not arrive: a build pointed at a backend that does not
 * mount it (404), a backend that is not running at all, and a backend that is
 * running but broken on this route (5xx). The last of those is not theoretical:
 * the deployed core answered 500 here and took the whole app down with it.
 *
 * The defaults are the cautious ones: nothing is blocked (an absent config
 * must not lock users out), and **every feature flag is false**, so the crypto
 * rails and the orderbook stay hidden rather than appearing and then 404ing —
 * which is the truthful answer for those two domains, not merely the safe one.
 */
export const FALLBACK_APP_CONFIG: AppConfig = {
  min_supported_version: '0.0.0',
  latest_version: '0.0.0',
  force_update: false,
  trading_enabled: true,
  kill_switch: { enabled: false, reason_fa: null },
  features: {
    orderbook_trading: false,
    crypto_deposit: false,
    crypto_withdrawal: false,
    credit_wallet: false,
    referral: false,
    tether_markets: false,
    support_chat: false,
    toman_withdrawal: false,
    toman_deposit: false,
  },
  links: { terms: '', privacy: '', support_phone: '' },
  support_chat: null,
  server_time: new Date().toISOString(),
}

/** Reported once per session, not once per retry. */
let missingConfigLogged = false

function reportMissingAppConfig(why: string): void {
  if (missingConfigLogged) return
  missingConfigLogged = true
  console.warn(
    `[api] GET /app/config could not be read (${why}). ` +
      'Falling back to defaults: kill switch off, force-update off, trading ' +
      'enabled, and every feature flag false. This is a missing answer, not ' +
      'a configuration choice — the flags the backend owns are currently ' +
      'decided by the client.',
  )
}

export const appConfigQuery = queryOptions({
  queryKey: ['app', 'config'] as const,
  queryFn: async ({ signal }) => {
    try {
      return await api.get('/app/config', { schema: appConfigSchema, auth: false, signal })
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        reportMissingAppConfig('404 — the backend does not mount it')
        return FALLBACK_APP_CONFIG
      }
      // A 5xx is an answer in name only. Nothing the client sends can change
      // it, and this call gates the first render, so treating it as fatal
      // trades a broken flag set for a blank screen.
      if (error instanceof ApiError && error.status >= 500) {
        reportMissingAppConfig(`${error.status} — the backend failed on it`)
        return FALLBACK_APP_CONFIG
      }
      // The route is always live, so there is no fixture to fall back to and an
      // unreachable backend would otherwise hang the app at boot.
      if (error instanceof NetworkError) {
        reportMissingAppConfig('the backend is unreachable')
        return FALLBACK_APP_CONFIG
      }
      throw error
    }
  },
  // A real config is good for five minutes. The fallback is not a configuration
  // at all — it is the absence of one — so it goes stale the moment it lands
  // and the next mount asks the backend again rather than living on defaults.
  staleTime: (query) => (query.state.data === FALLBACK_APP_CONFIG ? 0 : 5 * 60_000),
  // Everything a retry could fix — an unreachable backend, a 5xx — is answered
  // from the fallback above. What is left is a contract or auth failure, which
  // repeating will not change, and this call is what the first render waits on.
  retry: false,
})

/** `1.4.2` vs `1.2.0`, numeric segment by segment. */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const left = a.split('.').map((part) => Number.parseInt(part, 10) || 0)
  const right = b.split('.').map((part) => Number.parseInt(part, 10) || 0)
  const length = Math.max(left.length, right.length)
  for (let i = 0; i < length; i += 1) {
    const l = left[i] ?? 0
    const r = right[i] ?? 0
    if (l !== r) return l > r ? 1 : -1
  }
  return 0
}

export function isUpdateRequired(config: AppConfig, clientVersion: string): boolean {
  return config.force_update || compareVersions(clientVersion, config.min_supported_version) < 0
}
